/**
 * Dynamic configuration utility that calculates environment variables
 * based on the current application URL subdomain (only in production environments)
 */

interface DynamicConfig {
  entraIdRedirectUri: string;
  orderApiUrl: string;
  opsApiUrl: string;
  orderApiUrlNegotiateSignalR: string;
}

/**
 * Checks if we're in a local development environment
 * @returns true if in local development
 */
function isLocalDevelopment(): boolean {
  const hostname = window.location.hostname;
  return hostname === 'localhost' || hostname === '127.0.0.1';
}

/**
 * Gets the app type from URL parameters for local development
 * @returns 'oms', 'ops', or null if not specified
 */
function getAppFromUrlParams(): string | null {
  if (!isLocalDevelopment()) {
    return null;
  }
  
  const urlParams = new URLSearchParams(window.location.search);
  const appParam = urlParams.get('app');
  
  if (appParam === 'oms' || appParam === 'ops') {
    return appParam;
  }
  
  return null;
}

/**
 * Extracts the subdomain from the current URL
 * @returns The subdomain (e.g., "ops-tst", "oms-dev", "ops")
 */
function getCurrentSubdomain(): string {
  const hostname = window.location.hostname;
  
  // For local development, check URL parameters first
  if (isLocalDevelopment()) {
    const appParam = getAppFromUrlParams();
    if (appParam) {
      return appParam;
    }
  }
  
  // Extract subdomain from hostname (e.g., "ops-tst.pharo.com" -> "ops-tst")
  const parts = hostname.split('.');
  if (parts.length >= 3) {
    return parts[0];
  }
  
  // Fallback to first part if no subdomain structure
  return parts[0];
}

/**
 * Determines if the subdomain is an OMS subdomain
 * @param subdomain The subdomain to check
 * @returns true if it's an OMS subdomain
 */
function isOmsSubdomain(subdomain: string): boolean {
  return subdomain.startsWith('oms');
}

/**
 * Builds the base URL for the current subdomain
 * @param subdomain The subdomain
 * @returns [omsBaseUrl, opsBaseUrl] - URLs for OMS and OPS APIs respectively
 */
function buildBaseUrl(subdomain: string): [string, string] {
  const protocol = window.location.protocol;
  const hostname = window.location.hostname;
  
  // Calculate OMS URL (convert ops->oms if needed, otherwise keep as-is)
  const omsHostname = hostname.replace('ops', 'oms');
  const omsBaseUrl = `${protocol}//${omsHostname}`;
  
  // Calculate OPS URL (convert oms->ops if needed, otherwise keep as-is)  
  const opsHostname = hostname.replace('oms', 'ops');
  const opsBaseUrl = `${protocol}//${opsHostname}`;
  
  return [omsBaseUrl, opsBaseUrl];
}

/**
 * Calculates dynamic configuration based on the current URL
 * @returns Dynamic configuration object
 */
export function calculateDynamicConfig(): DynamicConfig {
  // For local development, return empty strings to indicate static .env values should be used
  if (isLocalDevelopment()) {
    return {
      entraIdRedirectUri: '',
      orderApiUrl: '',
      opsApiUrl: '',
      orderApiUrlNegotiateSignalR: '',
    };
  }

  const subdomain = getCurrentSubdomain();
  const [baseOmsUrl, baseOpsUrl] = buildBaseUrl(subdomain);
  const isOms = isOmsSubdomain(subdomain);
  
  return {
    entraIdRedirectUri: isOms ? `${baseOmsUrl}/order-create` : baseOpsUrl,
    orderApiUrl: `${baseOmsUrl}/api/v0.1`,
    opsApiUrl: `${baseOpsUrl}/api/v0.1`,
    orderApiUrlNegotiateSignalR: `${baseOmsUrl}/api/v0.1/broadcasts/negotiate`,
  };
}

/**
 * Gets the current subdomain for debugging/logging purposes
 * @returns The current subdomain
 */
export function getSubdomain(): string {
  if (isLocalDevelopment()) {
    return 'local';
  }
  return getCurrentSubdomain();
}

/**
 * Gets the current app type (ops/oms) based on subdomain or URL parameters
 * @returns 'ops', 'oms', or the subdomain if it doesn't match either
 */
export function getCurrentAppType(): string {
  
  const hostname = window.location.hostname;
  
  // For local development, check URL parameters first
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    const urlParams = new URLSearchParams(window.location.search);
    const appParam = urlParams.get('app');
    
    if (appParam === 'oms' || appParam === 'ops') {
      return appParam;
    }
  }
  
  // Extract subdomain from hostname (e.g., "ops-tst.pharo.com" -> "ops-tst")
  const parts = hostname.split('.');
  const subdomain = parts.length >= 3 ? parts[0] : parts[0];
  
  return subdomain;
}

/**
 * Checks if the current environment is an OPS environment
 * @returns true if it's an OPS environment
 */
export function isOpsEnvironment(): boolean {
  return getCurrentAppType().includes('ops');
}

/**
 * Checks if the current environment is an OMS environment
 * @returns true if it's an OMS environment
 */
export function isOmsEnvironment(): boolean {
  return getCurrentAppType().includes('oms');
}

/**
 * Checks if we should use dynamic configuration
 * @returns true if dynamic configuration should be used
 */
export function shouldUseDynamicConfig(): boolean {
  return !isLocalDevelopment();
} 